import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import ClickableDiv from 'dpl/common/components/ClickableDiv';
import DogPlaceholderSvg from 'dpl/common/components/DogPlaceholderSvg';
import HorizontalSlider from 'dpl/components/HorizontalSlider';
import SmartImage from 'dpl/components/SmartImage';
import { getPuppyModalUrl } from 'dpl/dog_modals/utils';
import usePuppyPageVariant from 'dpl/puppy_page/hooks/usePuppyPageVariant';
import { openPuppyPage } from 'dpl/puppy_page/utils/helpers';
import { capitalize } from 'dpl/shared/utils';
import { BreederProfilePuppiesPropTypes } from 'dpl/shared/utils/customPropTypes';

export default function BreederProfileCurrentPuppiesSection({
  onPuppyClick,
  validPuppies
}) {
  const puppyPageVariant = usePuppyPageVariant();
  const containsAdults = validPuppies.find(pup => pup.adult);
  const title = containsAdults ? 'Puppies and older dogs' : 'Puppies';

  return (
    <section className="BreederProfileCurrentPuppiesSection">
      <h5 className="mb6 f5">{title}</h5>

      <HorizontalSlider
        iconClassName="b bg-white ph3 pv3 br-100 box-shadow-strong"
        arrowClassName="pb1"
        leftArrowClassName="BreederProfileCurrentPuppiesSection__leftArrow"
        rightArrowClassName="BreederProfileCurrentPuppiesSection__rightArrow"
        persistentArrows
        containerClassName="row flex-nowrap"
      >
        {validPuppies.map(puppy => {
          const {
            id,
            name,
            gender,
            profile_photo_url: profilePhotoUrl,
            reserved,
            snoozed,
            status_with_date: statusWithDate,
            litter_id: litterId
          } = puppy;

          const unavailable = reserved || snoozed;

          const [availabilityColor, availabilityLabel] = unavailable
            ? ['red', 'Reserved']
            : ['green', 'Available'];

          const Component = puppyPageVariant.enabled ? ClickableDiv : 'a';

          const props = puppyPageVariant.enabled
            ? {
                onClick: () => {
                  openPuppyPage({ puppyId: id });
                }
              }
            : {
                href: getPuppyModalUrl(litterId, id),
                onClick: e => {
                  e.preventDefault();
                  onPuppyClick(id, litterId);
                }
              };

          return (
            <div key={id} className="col-auto ws-normal">
              <p
                className={`DescriptiveBadge--small f1 br1 dib absolute bg-white z-1 fw-medium mt2 mh2 ${availabilityColor}`}
              >
                {availabilityLabel}
              </p>
              <Component
                {...props}
                className="w-100 f0 dib grow-child"
                data-test-id={`puppy-${id}`}
              >
                <div className="overflow-hidden br3">
                  {profilePhotoUrl ? (
                    <SmartImage
                      Tag="div"
                      alt={name}
                      src={profilePhotoUrl}
                      className="BreederProfileCurrentPuppiesSection__image cover bg-center br3 child"
                      width="267px"
                      height="178px"
                      loadingClass="bg-light-gray"
                      lazy
                      crop
                    />
                  ) : (
                    <DogPlaceholderSvg
                      className="child bw1 br1 b--near-white ba hover-bg-near-white"
                      puppy
                      width="267px"
                      height="178px"
                    />
                  )}
                </div>
                <div
                  className={classnames('pv4 f3 min-w-100 w0', {
                    'o-50': unavailable
                  })}
                >
                  <p className="fw-medium truncate">
                    <span className={classnames({ strike: unavailable })}>
                      {name || 'Unnamed'}
                    </span>
                    {unavailable && <span className="i f2 ml2">Reserved</span>}
                  </p>
                  <p>
                    {capitalize(gender || '')} {statusWithDate}
                  </p>
                </div>
              </Component>
            </div>
          );
        })}
      </HorizontalSlider>
    </section>
  );
}

BreederProfileCurrentPuppiesSection.propTypes = {
  onPuppyClick: PropTypes.func.isRequired,
  validPuppies: PropTypes.arrayOf(
    PropTypes.shape(BreederProfilePuppiesPropTypes)
  ).isRequired
};
